import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { omit } from "lodash";
import { ZodError } from "zod";
import { RootState } from "@/core/store";
import { FACE_KEYS } from "@/domains/createCharacter/constants/face";
import {
  CUSTOMIZATION_FATHERS,
  CUSTOMIZATION_MOTHERS,
  Config,
  GenderEnum,
} from "@/domains/createCharacter/constants/general";
import { Identical } from "@/domains/createCharacter/constants/genetics";
import { HEAD_OVERLAY, HEAD_OVERLAY_KEYS } from "@/domains/createCharacter/constants/headOverlay";

export interface CreateCharacterState {
  init: Omit<CreateCharacterState, "personalData" | "config"> | null;
  step: number;
  stepValues: { index: number; title: string; isCorrect: boolean }[];

  config: Config | null;

  // данные
  personalData: {
    firstName: string;
    lastName: string;
    age: number;
    gender: GenderEnum;
  };
  personalDataError: ZodError["errors"] | null;
  // генетика
  identicalVariants: [Identical, Identical][];
  currentIdenticalVariant: number;
  currentIdentical: Identical;
  genetics: {
    father: number;
    mother: number;
    similarity: number;
    skin: number;
  };
  // внешность
  face: Record<string, number>;
  eyeColor: number;
  // прическа
  hair: {
    drawableId: number;
    color: number;
    highlightColor: number;
  };
  /* шляпа */
  hats: {
    propId: number;
    drawableId: number;
    textureId: number;
  };
  /* Штаны */
  legs: {
    componentId: number;
    drawableId: number;
    textureId: number;
    paletteId: number;
  };
  /* кроссы */
  shoes: {
    componentId: number;
    drawableId: number;
    textureId: number;
    paletteId: number;
  };
  /* рубаха */
  tops: {
    componentId: number;
    drawableId: number;
    textureId: number;
    paletteId: number;
  };

  /*
  В GTA Online (и GTA 5 в целом) Head Overlay — это слой текстур, который накладывается на лицо персонажа для изменения его внешности. Это касается таких параметров, как:

  - Blemishes (Изъяны) – прыщи, шрамы, родинки и другие дефекты кожи.
  - Facial Hair (Лицевые волосы) – борода, усы.
  - Eyebrows (Брови) – их форма и густота.
  - Ageing (Старение) – морщины, складки на коже.
  - Makeup (Макияж) – тени, помада, румяна и т. д.
  - Blush (Румянец) – красные щеки, эффект легкого загара.
  - Complexion (Тон кожи) – пигментация, веснушки, покраснения.
  - Sun Damage (Повреждение от солнца) – загар, ожоги.
  - Freckles (Веснушки) – добавляют эффект веснушек на лицо.
  - Chest Hair (Волосы на груди) – для мужских персонажей.
  - Lipstick (Помада) – изменяет цвет и стиль губ.
  - Moles & Freckles (Родинки и веснушки) – добавляет дополнительные детали на кожу.
*/
  headOverlay: Record<
    string,
    {
      overlayId: number;
      index: number;
      firstColor: number;
      secondColor: number;
      opacity: number;
    }
  >;
}

const initialState: CreateCharacterState = {
  init: null,
  step: 0,
  stepValues: [
    {
      index: 0,
      title: "Данные",
      isCorrect: false,
    },
    {
      index: 1,
      title: "Генетика",
      isCorrect: false,
    },
    {
      index: 2,
      title: "Внешность",
      isCorrect: false,
    },
    {
      index: 3,
      title: "Прическа",
      isCorrect: false,
    },
    {
      index: 4,
      title: "Кожа",
      isCorrect: false,
    },
    {
      index: 5,
      title: "Одежда",
      isCorrect: false,
    },
  ],

  config: null,
  // config: {
  //   [GenderEnum.Male]: {
  //     hairs: [],
  //     hats: [],
  //     legs: [],
  //     shoes: [],
  //     tops: [],
  //   },
  //   [GenderEnum.Female]: {
  //     hairs: [],
  //     hats: [],
  //     legs: [],
  //     shoes: [],
  //     tops: [],
  //   },
  // },

  // персональные данные
  personalData: {
    firstName: "",
    lastName: "",
    age: null,
    gender: GenderEnum.Male,
  },
  personalDataError: null,
  // генетика
  identicalVariants: [
    [Identical.Father, Identical.Mother],
    [Identical.Father, Identical.GrandFather],
    [Identical.Mother, Identical.GrandMother],
  ],
  currentIdenticalVariant: 0,
  currentIdentical: Identical.Father,
  genetics: {
    father: CUSTOMIZATION_FATHERS[0],
    mother: CUSTOMIZATION_MOTHERS[0],
    similarity: Math.random(),
    skin: Math.random(),
  },

  // внешность
  face: Object.fromEntries(FACE_KEYS.map((feature) => [feature, 0])),
  eyeColor: 0,

  // прическа
  headOverlay: Object.fromEntries(
    HEAD_OVERLAY_KEYS.map((item) => [
      item,
      {
        overlayId: HEAD_OVERLAY[item].id,
        index: 255,
        firstColor: 0,
        secondColor: 0,
        opacity: 1,
      },
    ]),
  ),
  hair: {
    drawableId: 0,
    color: 0,
    highlightColor: 0,
  },
  hats: {
    propId: 0,
    drawableId: 0,
    textureId: 0,
  },
  legs: {
    componentId: 0,
    drawableId: 0,
    textureId: 0,
    paletteId: 0,
  },
  shoes: {
    componentId: 0,
    drawableId: 0,
    textureId: 0,
    paletteId: 0,
  },
  tops: {
    componentId: 0,
    drawableId: 0,
    textureId: 0,
    paletteId: 0,
  },
};

const createCharacterSlice = createSlice({
  name: "createCharacter",
  initialState,
  reducers: {
    setConfig(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["config"]>>,
    ) {
      state.config = { ...state.config, ...action.payload };
    },
    setPersonalData(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["personalData"]>>,
    ) {
      state.personalData = { ...state.personalData, ...action.payload };
    },
    setPersonalDataErrors: (
      state: CreateCharacterState,
      action: PayloadAction<ZodError["errors"] | undefined>,
    ) => {
      state.personalDataError = action?.payload || null;
    },
    setIdenticalVariant(state: CreateCharacterState, action: PayloadAction<number>) {
      state.currentIdenticalVariant = action.payload;
    },
    setIdentical(state: CreateCharacterState, action: PayloadAction<Identical>) {
      state.currentIdentical = action.payload;
    },
    setGenetics(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["genetics"]>>,
    ) {
      state.genetics = { ...state.genetics, ...action.payload };
    },
    setFace(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["face"]>>,
    ) {
      state.face = { ...state.face, ...action.payload };
    },
    setEyeColor(state: CreateCharacterState, action: PayloadAction<number>) {
      state.eyeColor = action.payload;
    },
    setHeadOverlay(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["headOverlay"]>>,
    ) {
      state.headOverlay = { ...state.headOverlay, ...action.payload };
    },
    setHair(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["hair"]>>,
    ) {
      state.hair = { ...state.hair, ...action.payload };
    },
    setHats(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["hats"]>>,
    ) {
      state.hats = { ...state.hats, ...action.payload };
    },
    setShoes(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["shoes"]>>,
    ) {
      state.shoes = { ...state.shoes, ...action.payload };
    },
    setLegs(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["legs"]>>,
    ) {
      state.legs = { ...state.legs, ...action.payload };
    },
    setTops(
      state: CreateCharacterState,
      action: PayloadAction<Partial<CreateCharacterState["tops"]>>,
    ) {
      state.tops = { ...state.tops, ...action.payload };
    },
    setStep(state: CreateCharacterState, action: PayloadAction<number>) {
      state.step = action.payload;
    },
    prevStep: (state) => {
      if (state.step !== 0) {
        state.step -= 1;
      }
    },
    nextStep: (state) => {
      if (state.step !== state.stepValues.length - 1) {
        state.step += 1;
      }
    },
    setCorrectStep: (state, action: PayloadAction<{ step: number; isCorrect: boolean }>) => {
      const { payload } = action;
      const newStepValues = [...state.stepValues];
      newStepValues[payload.step].isCorrect = payload.isCorrect;
      state.stepValues = newStepValues;
    },
    reset: (state) => {
      if (!state.config) return state;

      return {
        ...state,
        ...omit(initialState, ["personalData", "config"]),
        hair: {
          drawableId: state.config[state.personalData.gender].hairs[0],
          color: 0,
          highlightColor: 0,
        },
        hats: state.config[state.personalData.gender].hats[0],
        tops: state.config[state.personalData.gender].tops[0],
        legs: state.config[state.personalData.gender].legs[0],
        shoes: state.config[state.personalData.gender].shoes[0],
      };
    },
  },
});

export const personalDataSelector = (state: RootState) => state.createCharacter.personalData;
export const personalDataErrorsSelector = (state: RootState) =>
  state.createCharacter.personalDataError;
export const personalDataSeparatedErrorsSelector = (state: RootState) => {
  if (state.createCharacter.personalDataError) {
    const errors = state.createCharacter.personalDataError;
    const firstNameError = errors.find((item) => item.path[0] === "firstName");
    const lastNameError = errors.find((item) => item.path[0] === "lastName");
    const ageError = errors.find((item) => item.path[0] === "age");
    return {
      firstName: firstNameError ? firstNameError.message : "",
      lastName: lastNameError ? lastNameError.message : "",
      age: ageError ? ageError.message : "",
    };
  }
  return {
    firstName: "",
    lastName: "",
    age: "",
  };
};

export const identicalVariantsSelector = (state: RootState) =>
  state.createCharacter.identicalVariants;
export const currentIdenticalVariantSelector = (state: RootState) =>
  state.createCharacter.currentIdenticalVariant;
export const currentIdenticalSelector = (state: RootState) =>
  state.createCharacter.currentIdentical;
export const geneticsSelector = (state: RootState) => state.createCharacter.genetics;

export const faceSelector = (state: RootState) => state.createCharacter.face;
export const eyeColorSelector = (state: RootState) => state.createCharacter.eyeColor;
export const headOverlaySelector = (state: RootState) => state.createCharacter.headOverlay;

export const hairSelector = (state: RootState) => state.createCharacter.hair;
export const hatsSelector = (state: RootState) => state.createCharacter.hats;
export const legsSelector = (state: RootState) => state.createCharacter.legs;
export const shoesSelector = (state: RootState) => state.createCharacter.shoes;
export const topsSelector = (state: RootState) => state.createCharacter.tops;

export const stepSelector = (state: RootState) => state.createCharacter.step;
export const stepValuesSelector = (state: RootState) => state.createCharacter.stepValues;
export const configSelector = createSelector(
  (state: RootState) => state.createCharacter.config,
  (_, gender?: GenderEnum) => gender,
  (config, gender) => {
    if (gender) {
      return config ? config[gender] : null;
    }
    return config;
  },
);

export const isReadyToCreateCharacterSelector = (state: RootState) => {
  return !state.createCharacter.stepValues.some((item) => !item.isCorrect);
};

export const createCharacterReducer = createCharacterSlice.reducer;

export const {
  setConfig,
  setStep,
  setGenetics,
  setFace,
  setEyeColor,
  setHeadOverlay,
  setHair,
  setHats,
  setShoes,
  setLegs,
  setTops,
  setPersonalData,
  prevStep,
  nextStep,
  setIdenticalVariant,
  setIdentical,
  reset,
  setCorrectStep,
  setPersonalDataErrors,
} = createCharacterSlice.actions;
